import classNames from 'classnames';
import { format, getMonth, getYear, subDays, addDays } from 'date-fns';
import { range } from 'lodash';
import { FC, useState, useEffect, useMemo, useCallback } from 'react';
import DatePicker, {
  CalendarContainer,
  ReactDatePickerProps,
} from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FieldRenderProps } from 'react-final-form';

import { parseDateForPicker } from '../../../utilities/parseDateForPicker';
import { PrimaryButton, SecondaryButton } from '../../Button';

const years = range(2000, getYear(new Date()) + 1, 1);
const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const pastRanges = [
  { label: 'Past 3 days', range: [subDays(new Date(), 3), new Date()] },
  { label: 'Past week', range: [subDays(new Date(), 7), new Date()] },
  { label: 'Past 2 weeks', range: [subDays(new Date(), 14), new Date()] },
  { label: 'Past month', range: [subDays(new Date(), 30), new Date()] },
];

const upcomingRanges = [
  { label: 'Upcoming week', range: [new Date(), addDays(new Date(), 7)] },
  { label: 'Upcoming 2 weeks', range: [new Date(), addDays(new Date(), 14)] },
  { label: 'Upcoming month', range: [new Date(), addDays(new Date(), 30)] },
];

type TableFilterDateRangeFieldProps = ReactDatePickerProps &
  FieldRenderProps<
    string,
    HTMLElement,
    {
      start: string;
      end?: string;
    }
  > & {
    value?: {
      start: string;
      end?: string;
    };
    labelText: string;
  };

const TableFilterDateRangeField: FC<TableFilterDateRangeFieldProps> = ({
  input,
  labelText,
  ...props
}) => {
  const [startDate, setStartDate] = useState<Date | null>(
    parseDateForPicker(input.value?.start),
  );
  const [endDate, setEndDate] = useState<Date | null>(
    parseDateForPicker(input.value?.end),
  );
  const [isOpen, setIsOpen] = useState<boolean>(false);

  useEffect(() => {
    if (!input.value) {
      setStartDate(null);
      setEndDate(null);
    }
  }, [input.value]);

  const handleRangeChange = useCallback(
    (dates: [Date | null, Date]) => {
      const [start, end] = dates;
      setStartDate(start);
      setEndDate(end);

      if (start && !end) {
        input.onChange({
          start: format(start, 'yyyy-MM-dd'),
          end: undefined,
        });
        return;
      }

      if (start && end) {
        input.onChange({
          start: format(start, 'yyyy-MM-dd'),
          end: format(end, 'yyyy-MM-dd'),
        });
        return;
      }

      if (!start && !end) {
        input.onChange(undefined);
        return;
      }
    },
    [input],
  );

  const renderQuickButtons = useMemo(
    () => (
      <div className="my-2 flex w-64 flex-col divide-y divide-gray-200">
        <div>
          <div className="scrollbar-hide mb-1 flex cursor-grab gap-2 overflow-scroll px-2">
            {pastRanges.map(({ label, range }) => (
              <SecondaryButton
                key={label}
                onClick={(e) => {
                  e.preventDefault();
                  handleRangeChange(range as [Date, Date]);
                }}
              >
                {label}
              </SecondaryButton>
            ))}
          </div>
        </div>
        <div>
          <div className="scrollbar-hide mt-1 flex cursor-grab gap-2 overflow-scroll px-2">
            {upcomingRanges.map(({ label, range }) => (
              <PrimaryButton
                key={label}
                onClick={(e) => {
                  e.preventDefault();
                  handleRangeChange(range as [Date, Date]);
                }}
              >
                {label}
              </PrimaryButton>
            ))}
          </div>
        </div>
      </div>
    ),
    [handleRangeChange],
  );

  return (
    <div
      className={classNames('flex flex-1 relative  z-10', { 'z-20': isOpen })}
    >
      <label
        htmlFor={input.name}
        className="absolute -top-2 left-2 z-10 inline-block bg-[linear-gradient(to_bottom,_var(--tw-gradient-stops))] from-gray-50 to-white px-1 text-xs font-medium text-gray-500"
      >
        {labelText || input.name}
      </label>
      <DatePicker
        {...props}
        id={input.name}
        selected={startDate}
        startDate={startDate}
        endDate={endDate}
        selectsRange
        className="block w-full rounded-md border border-gray-300 bg-white py-2 pr-3 text-sm shadow-sm"
        dateFormat="M/d/y"
        onCalendarOpen={() => setIsOpen(true)}
        onCalendarClose={() => setIsOpen(false)}
        wrapperClassName="w-full"
        isClearable
        clearButtonClassName="after:!bg-secondaryBlue"
        onChange={handleRangeChange}
        renderCustomHeader={({ date, changeYear, changeMonth }) => (
          <div className="m-2 flex gap-2">
            <select
              className="block w-full rounded-md border-gray-300 text-sm shadow-sm"
              value={months[getMonth(date)]}
              onChange={({ target: { value } }) =>
                changeMonth(months.indexOf(value))
              }
            >
              {months.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <select
              className="block w-full rounded-md border-gray-300 text-sm shadow-sm"
              value={getYear(date)}
              onChange={({ target: { value } }) => {
                changeYear(Number(value));
              }}
            >
              {years.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        )}
        calendarContainer={({ className, children }) => (
          <CalendarContainer className={className}>
            <div className="relative flex flex-col">
              {children}
              <div className="border-b border-gray-200"></div>
              {renderQuickButtons}
            </div>
          </CalendarContainer>
        )}
      />
    </div>
  );
};

export default TableFilterDateRangeField;
